import * as config from 'config';

import { ICaptchaResponse } from 'models/captcha';
import {
  CustomerAccountStatus,
  ICustomerUpdate,
  ICustomerChangeEmailResponse,
  ICustomerDeleteAccountResponse,
  CustomerConsentType,
  CustomerResponse,
  Customer,
  EmailType
} from 'models/customer';
import { CONFIRM_CHANGE_EMAIL, CONFIRM_DELETE_ACCOUNT } from 'router/routes';
import {
  apiFetch,
  buildMetaForward,
  createQueryUrl,
  defaultHeaders
} from 'utils/fetchUtils/fetchUtils';

export async function generateCaptcha(email: string): Promise<string> {
  const url = `${config.backendApi}/captcha/generate`;

  const res = await apiFetch<ICaptchaResponse>(url, {
    headers: defaultHeaders,
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'captcha',
        attributes: {
          email
        }
      }
    })
  });

  return res.data.attributes.svg;
}

export async function getAccountStatusByEmail(
  email: string
): Promise<CustomerAccountStatus> {
  const url = `${
    config.backendApi
  }/customers/account-status/${encodeURIComponent(email)}`;

  const json = await apiFetch<any>(url, {
    headers: defaultHeaders,
    method: 'GET'
  });

  return json.data.attributes;
}

export async function resetPasswordByEmail(
  email: string,
  languageCode: string
): Promise<void> {
  const url = createQueryUrl(
    `${config.backendApi}/password-recovery/${encodeURIComponent(email)}`,
    { language: languageCode }
  );

  await apiFetch(url, {
    headers: defaultHeaders,
    method: 'POST'
  });
}

export async function validateChangeEmail(
  customerId: string,
  code: string
): Promise<ICustomerChangeEmailResponse> {
  const url = `${config.backendApi}/customers/${customerId}/validate-change-email/${code}`;

  return await apiFetch(url, {
    headers: defaultHeaders,
    method: 'GET'
  });
}

export async function validateDeleteAccount(
  customerId: string,
  code: string
): Promise<ICustomerDeleteAccountResponse> {
  const url = `${config.backendApi}/customers/${customerId}/validate-delete-account/${code}`;

  return await apiFetch(url, {
    headers: defaultHeaders,
    method: 'GET'
  });
}

export async function getConsentType(): Promise<CustomerConsentType> {
  const url = `${config.backendApi}/customers/consents/consent-types`;

  const json = await apiFetch<any>(url, {
    headers: defaultHeaders,
    method: 'GET'
  });

  return {
    id: json.data.id,
    texts: json.data.attributes.texts || {}
  };
}

// Protected operations for authenticated users
export async function getCustomer(customerId: string): Promise<Customer> {
  const url = `${config.backendApi}/customers/${customerId}`;

  const json = await apiFetch<CustomerResponse>(url, {
    headers: defaultHeaders
  });

  return json.data;
}

export async function updateCustomer(
  customerId: string,
  newCustomer: ICustomerUpdate
): Promise<Customer> {
  const url = `${config.backendApi}/customers/${customerId}`;

  const json = await apiFetch<CustomerResponse>(url, {
    headers: defaultHeaders,
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'customer',
        attributes: {
          ...newCustomer
        }
      }
    })
  });

  return json.data;
}

export async function requestChangeEmail(
  customerId: string,
  emailType: EmailType,
  newEmail: string,
  password: string
): Promise<void> {
  const url = `${config.backendApi}/customers/${customerId}/change-email`;

  return await apiFetch(url, {
    headers: defaultHeaders,
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'customer',
        attributes: {
          newEmail,
          emailType,
          password
        }
      },
      meta: {
        brand: config.brand,
        forward: buildMetaForward(CONFIRM_CHANGE_EMAIL)
      }
    })
  });
}

export async function confirmChangeEmail(
  customerId: string,
  newEmail: string,
  code: string,
  password: string,
  emailType: EmailType
): Promise<void> {
  const url = `${config.backendApi}/customers/${customerId}/confirm-change-email`;

  await apiFetch(url, {
    headers: {
      ...defaultHeaders
    },
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'customer',
        attributes: {
          code,
          newEmail,
          password,
          emailType
        }
      }
    })
  });
}

export async function requestDeleteAccount(customerId: string): Promise<void> {
  const url = `${config.backendApi}/customers/${customerId}/request-delete-account`;

  return await apiFetch(url, {
    headers: defaultHeaders,
    method: 'POST',
    body: JSON.stringify({
      meta: {
        brand: config.brand,
        forward: buildMetaForward(CONFIRM_DELETE_ACCOUNT)
      }
    })
  });
}

export async function confirmDeleteAccount(
  customerId: string,
  code: string,
  password: string
): Promise<void> {
  const url = `${config.backendApi}/customers/${customerId}/confirm-delete-account`;

  await apiFetch(url, {
    headers: {
      ...defaultHeaders
    },
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'customer',
        attributes: {
          code,
          password
        }
      }
    })
  });
}
